import React from "react";
import { useMap } from "@vis.gl/react-google-maps";
import { SurfaceType } from "./DataAndMap";
import { SimpleFeatureVisibility } from "./Core";
import { applySaturationToHexColor } from "./utils/colors";

export const MAP_ZINDEX_SELECTED = 1000;
export const MAP_ZINDEX_LABEL = 750;
export const MAP_ZINDEX_ARROW = 500;
const MAP_ZINDEX_SURFACE_OTHER = 40;
const MAP_ZINDEX_SURFACE_SNOW_PILE = 30;
const MAP_ZINDEX_SURFACE_SIDEWALK = 20;
const MAP_ZINDEX_SURFACE_PARKING_LOT = 10;
const MAP_ZINDEX_SURFACE_BOUNDARY = 1;

// TODO: this type needs improving
type StringSurfaceType =
  | "parkingLot"
  | "sidewalk"
  | "snowPile"
  | "other"
  | "boundary";

// Extended properties type that includes both surface and label features
export type ExtendedFeatureProperties = {
  name?: string;
  surfaceType?: StringSurfaceType;
  editing?: boolean;
  selected?: boolean;
  // id?: number;
};

// Type-safe property getter helper
function getTypedProperty<K extends keyof ExtendedFeatureProperties>(
  feature: google.maps.Data.Feature,
  name: K,
): ExtendedFeatureProperties[K] {
  return feature.getProperty(name) as ExtendedFeatureProperties[K];
}

const surfaceTypeOrder = (surfaceType: StringSurfaceType) => {
  switch (surfaceType) {
    case "parkingLot":
      return MAP_ZINDEX_SURFACE_PARKING_LOT;
    case "sidewalk":
      return MAP_ZINDEX_SURFACE_SIDEWALK;
    case "snowPile":
      return MAP_ZINDEX_SURFACE_SNOW_PILE;
    case "other":
      return MAP_ZINDEX_SURFACE_OTHER;
    case "boundary":
      return MAP_ZINDEX_SURFACE_BOUNDARY;
    default:
      return 5;
  }
};

export function DataStyling({
  surfaceTypes,
  surfaceData,
  featureVisibility,
}: {
  featureVisibility: SimpleFeatureVisibility;
  surfaceData: google.maps.Data;
  surfaceTypes: SurfaceType[];
}) {
  const map = useMap();

  React.useEffect(() => {
    if (!map || !map.data) return;

    // Set the style for the data layer
    surfaceData.setStyle((feature: google.maps.Data.Feature) => {
      const surfaceType = getTypedProperty(feature, "surfaceType");
      const editing = getTypedProperty(feature, "editing");
      const selected = getTypedProperty(feature, "selected");

      // Check visibility based on feature type
      let isVisible = true;

      if (featureVisibility && surfaceType) {
        isVisible =
          featureVisibility[surfaceType] !== undefined
            ? featureVisibility[surfaceType]
            : true;
      }

      // If not visible, return style that hides the feature
      if (!isVisible) {
        return {
          visible: false,
          fillOpacity: 0,
          strokeOpacity: 0,
        };
      }

      // Set color based on feature type
      let featureColor: string;
      let fillOpacity = selected ? 0.75 : 0.5;

      if (surfaceType && surfaceType !== "boundary") {
        const surfaceTypeConfig = surfaceTypes.find(
          (type) => type.value === surfaceType,
        );
        featureColor = surfaceTypeConfig?.color || "blue";
      } else if (surfaceType === "boundary") {
        featureColor = "red";
        fillOpacity = 0.25;
      } else {
        featureColor = "blue";
      }

      return {
        draggable: editing && selected,
        editable: editing && selected,
        fillColor: featureColor,
        strokeColor:
          featureColor.charAt(0) === "#"
            ? applySaturationToHexColor(featureColor, 100)
            : featureColor,
        strokeWeight: selected ? 6 : 4,
        visible: true,
        fillOpacity,
        strokeOpacity: 1,

        /**
         * All features are displayed on the map in order of their zIndex, with
         * higher values displaying in front of features with lower values. Markers
         * are always displayed in front of line-strings and polygons.
         */
        // z-index should change for selected item, otherwise we'd have a switch statement for this
        zIndex: surfaceType
          ? surfaceTypeOrder(surfaceType) + (selected ? 1000 : 0)
          : 5,
      };
    });
  }, [map, surfaceData, featureVisibility, surfaceTypes]);

  return null;
}
